import React, { useEffect, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import styles from './DailyConversations.module.scss';
import { dashboardEndpoints } from 'hooks/endpoints';
import { useFetch } from 'hooks/useFetch';
import moment from 'moment';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import ChartModal from '../ChartModal';
import FilterDropdown from 'component/Custom/FilterDropdown/FilterDropdown';
import NoData from 'component/Custom/NoData/NoData';

const DailyConversations = ({
  brandingData,
  botId,
  startDate,
  endDate,
  days,
}) => {
  const chartRef = useRef(null);
  const [series, setSeries] = useState([]);
  const [modalSeries, setModalSeries] = useState([]);
  const [showChartModal, setShowChartModal] = useState(false);

  const [modalDays, setModalDays] = useState(null);
  const [modalStartDate, setModalStartDate] = useState(null);
  const [modalEndDate, setModalEndDate] = useState(null);

  const { data: conversationsPerDay, refetch: refetchConversationsPerDay } =
    useFetch(
      ['conversationsPerDay', startDate, endDate, botId],
      dashboardEndpoints.getConversationsPerDay(startDate, endDate),
      { enabled: false },
      botId ? { botId: botId } : {}
    );

  const {
    data: conversationsPerDayModal,
    refetch: refetchConversationsPerDayModal,
  } = useFetch(
    ['conversationsPerDayModal', modalStartDate, modalEndDate, botId],
    dashboardEndpoints.getConversationsPerDay(modalStartDate, modalEndDate),
    { enabled: false },
    botId ? { botId: botId } : {}
  );

  useEffect(() => {
    refetchConversationsPerDay();
  }, [startDate, endDate]);

  useEffect(() => {
    setModalDays(days);
  }, [days]);

  useEffect(() => {
    if (!showChartModal) return;
    if (!modalStartDate || !modalEndDate) return;
    refetchConversationsPerDayModal();
  }, [modalStartDate, modalEndDate, showChartModal]);

  useEffect(() => {
    if (conversationsPerDay?.data?.length) {
      const data = conversationsPerDay.data.map((d) => ({
        x: new Date(d.date).getTime(),
        y: d.chatCount ?? 0,
      }));
      setSeries([{ name: 'Conversations', data }]);
    } else {
      setSeries([]);
    }
  }, [conversationsPerDay]);

  useEffect(() => {
    if (conversationsPerDayModal?.data?.length) {
      const data = conversationsPerDayModal.data.map((d) => ({
        x: new Date(d.date).getTime(),
        y: d.chatCount ?? 0,
      }));
      setModalSeries([{ name: 'Conversations', data }]);
    } else {
      setModalSeries([]);
    }
  }, [conversationsPerDayModal]);

  const brand = brandingData?.page_ButtonColor || '#0744E1';

  const baseOptions = {
    chart: {
      type: 'area',
      height: 350,
      toolbar: { show: false },
      background: '#FFFFFF',
      zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: 'straight',
      width: 4,
      lineCap: 'round',
      colors: [brand],
    },
    markers: { size: 0, hover: { size: 0 } },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.85,
        opacityTo: 0.5,
        stops: [0, 90, 100],
      },
      colors: [brand],
    },
    xaxis: {
      type: 'datetime',
      labels: {
        format: 'dd-MMM',
        style: {
          fontSize: '12px',
          color: '#000',
          fontWeight: 400,
          fontFamily: 'Poppins, sans-serif',
        },
      },
      axisBorder: { show: true, color: '#222' },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        offsetX: -15,
        style: {
          fontSize: '12px',
          color: '#000',
          fontWeight: 400,
          fontFamily: 'Poppins, sans-serif',
        },
      },
    },
    grid: {
      borderColor: '#E9EEF5',
      strokeDashArray: 4,
      padding: { top: 4, right: 4, bottom: 4, left: 4 },
    },
    tooltip: {
      x: { formatter: (val) => moment(val).format('DD-MMM') },
    },
    legend: { show: false },
  };

  const mainChartOptions = {
    ...baseOptions,
    chart: { ...baseOptions.chart, id: 'daily-conversations-chart' },
  };

  const modalChartOptions = {
    ...baseOptions,
    chart: { ...baseOptions.chart, id: 'daily-conversations-modal-chart' },
  };

  const toCSV = (points) => {
    const header = 'Date,Conversations';
    const lines = (points || []).map((p) => {
      const date = moment(p.x).format('DD-MMM');
      const value = p.y ?? 0;
      return `${date},${value}`;
    });
    return [header, ...lines].join('\n');
  };

  const downloadCSV = (filename, csv) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownload = () => {
    if (!series.length) return;
    const csv = toCSV(series[0].data || []);
    downloadCSV('daily_conversations.csv', csv);
  };

  const handleDownloadModal = () => {
    if (!modalSeries.length) return;
    const csv = toCSV(modalSeries[0].data || []);
    downloadCSV('daily_conversations.csv', csv);
  };

  return (
    <div ref={chartRef} className={styles['chart-container']}>
      <div className={styles['actions-container']}>
        <div className={styles['title-container']}>
          <span className={styles['title']}>Number of Daily Conversations</span>
          <Tooltip
            content='Total chatbot conversations initiated each day, 
showing user interaction volume over time.'
            className={styles['tooltip']}
          >
            <SvgWithBranding type='info' color='#0744E1' width={22} />
          </Tooltip>
        </div>
        <div className={styles['actions-container']}>
          {series.length === 0 ? (
            <Tooltip content='No data available to export'>
              <SvgWithBranding
                className={`${styles['download-icon']} ${styles['disabled']}`}
                type='download2'
                onClick={() => {}}
              />
            </Tooltip>
          ) : (
            <SvgWithBranding
              className={`${styles['download-icon']}`}
              type='download2'
              onClick={handleDownload}
            />
          )}
          <SvgWithBranding
            className={styles['download-icon']}
            type='maximize'
            onClick={() => {
              setModalStartDate((prev) => prev || startDate);
              setModalEndDate((prev) => prev || endDate);
              setShowChartModal(true);
            }}
          />
        </div>
      </div>

      {series.length === 0 ? (
        <NoData message='No Conversations Recorded' minHeight={450} />
      ) : (
        <ReactApexChart
          options={mainChartOptions}
          series={series}
          type='area'
          height={470}
        />
      )}

      <ChartModal
        show={showChartModal}
        handleClose={() => setShowChartModal(false)}
        modalBodyClassName={styles['modal']}
        child={
          <>
            <div className={styles['actions-container']}>
              <span className={styles['modal-title']}>
                Number of Daily Conversations
              </span>
              <div className={styles['actions']}>
                <FilterDropdown
                  days={modalDays ?? days}
                  handleSetDays={setModalDays}
                  handleOpenCustomDates={() => {}}
                  startDate={modalStartDate}
                  setStartDate={setModalStartDate}
                  endDate={modalEndDate}
                  setEndDate={setModalEndDate}
                  variant='secondary'
                />
                {modalSeries.length === 0 ? (
                  <Tooltip content='No data available to export'>
                    <SvgWithBranding
                      className={`${styles['download-icon']} ${styles['disabled']}`}
                      type='download2'
                      onClick={() => {}}
                    />
                  </Tooltip>
                ) : (
                  <SvgWithBranding
                    className={`${styles['download-icon']}`}
                    type='download2'
                    onClick={handleDownloadModal}
                  />
                )}
                <SvgWithBranding
                  className={styles['close-icon']}
                  type='exit_modal'
                  onClick={() => {
                    setModalStartDate(null);
                    setModalEndDate(null);
                    setModalDays(days);
                    setShowChartModal(false);
                  }}
                />
              </div>
            </div>

            {modalSeries.length === 0 ? (
              <NoData message='No Conversations Recorded' minHeight={450} />
            ) : (
              <ReactApexChart
                options={modalChartOptions}
                series={modalSeries}
                type='area'
                height={470}
              />
            )}
          </>
        }
      />
    </div>
  );
};

export default DailyConversations;
